import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Pencil,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Receipt,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { usdCompact } from '@/lib/format';
import type {
  RenovationItemDto,
  RenovationItemStatus,
  RenovationExpenseDto,
  RenovationRoomDto,
} from '@cc/types';
import { RenovationChat } from '@/components/RenovationChat';
import { RoomMediaPanel } from '@/components/renovation/RoomMediaPanel';
import { HeroPinBoard, type HeroPinBoardHandle } from '@/components/renovation/HeroPinBoard';
import { BudgetRollupCard } from '@/components/renovation/BudgetRollupCard';
import { LookAheadList } from '@/components/renovation/LookAheadList';
import { MilestoneStrip } from '@/components/renovation/MilestoneStrip';
import { RoomColorWheel } from '@/components/renovation/RoomColorWheel';
import { ConsolidationView } from '@/components/renovation/ConsolidationView';
import { useMediaPins } from '@/hooks/useMediaPins';

const STATUSES: RenovationItemStatus[] = [
  'NOT_STARTED',
  'PLANNING',
  'ORDERED',
  'DELIVERED',
  'IN_PROGRESS',
  'INSTALLED',
  'COMPLETE',
  'BLOCKED',
];

const STATUS_VARIANT: Record<RenovationItemStatus, 'success' | 'warning' | 'muted' | 'danger' | 'default'> = {
  NOT_STARTED: 'muted',
  PLANNING: 'default',
  ORDERED: 'default',
  DELIVERED: 'default',
  IN_PROGRESS: 'success',
  INSTALLED: 'success',
  COMPLETE: 'success',
  BLOCKED: 'danger',
};

/**
 * Row backgrounds give Karl an at-a-glance read on each line item:
 *   hard green     = COMPLETE (done)
 *   subtle green   = ORDERED / DELIVERED / IN_PROGRESS / INSTALLED (in motion)
 *   subtle orange  = has a vendor lined up but not yet ordered
 *   subtle red     = no vendor / solution yet (still scoping)
 *   stronger red   = BLOCKED
 */
function rowSignalClass(item: RenovationItemDto): string {
  if (item.status === 'COMPLETE') return 'bg-emerald-500/30';
  if (
    item.status === 'ORDERED' ||
    item.status === 'DELIVERED' ||
    item.status === 'IN_PROGRESS' ||
    item.status === 'INSTALLED'
  ) {
    return 'bg-emerald-500/15';
  }
  if (item.status === 'BLOCKED') return 'bg-rose-500/25';
  // NOT_STARTED or PLANNING — distinguish on whether a vendor exists.
  const hasVendor = (item.vendor ?? '').trim().length > 0;
  return hasVendor ? 'bg-amber-500/15' : 'bg-rose-500/15';
}

export function RenovationPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: qk.renovation(projectId ?? ''),
    queryFn: () => api.getRenovation(projectId!),
    enabled: Boolean(projectId),
  });

  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [view, setView] = useState<'rooms' | 'schedule' | 'consolidation'>('rooms');
  const heroRef = useRef<HeroPinBoardHandle | null>(null);

  // Auto-pick first room.
  useEffect(() => {
    if (data?.rooms.length && !activeRoomId) {
      setActiveRoomId(data.rooms[0]!.id);
    } else if (data && !data.rooms.find((r) => r.id === activeRoomId)) {
      setActiveRoomId(data.rooms[0]?.id ?? null);
    }
  }, [data, activeRoomId]);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: qk.renovation(projectId!) });

  if (!projectId) return <p>Bad route.</p>;
  if (error) return <p className="text-sm text-destructive">Failed to load renovation hub.</p>;
  if (isLoading || !data) return <Skeleton className="h-96 w-full" />;

  const items = activeRoomId ? data.items.filter((i) => i.roomId === activeRoomId) : [];
  const activeRoom = data.rooms.find((r) => r.id === activeRoomId) ?? null;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/projects/${projectId}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> {data.project.name}
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {data.project.name} renovation
            </h1>
          </div>
          <AddRoomButton projectId={projectId} onCreated={invalidate} />
        </div>
      </div>

      <BudgetRollupCard
        scope="project"
        label="Project budget"
        rollup={data.budget.byProject}
        budgetTotal={data.budget.total}
      />

      <EmailExtractedExpenses expenses={data.expenses} />

      {data.rooms.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
            <p className="text-sm">No rooms yet.</p>
            <p className="max-w-md text-xs text-muted-foreground">
              Add a room to start tracking activities. Rooms organise the work; items are the
              spreadsheet rows within each room.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-1 border-b border-border">
            <button
              onClick={() => setView('rooms')}
              className={cn(
                'border-b-2 px-3 py-1.5 text-sm transition-colors',
                view === 'rooms'
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              Rooms
            </button>
            <button
              onClick={() => setView('schedule')}
              className={cn(
                'border-b-2 px-3 py-1.5 text-sm transition-colors',
                view === 'schedule'
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              Schedule
            </button>
            <button
              onClick={() => setView('consolidation')}
              className={cn(
                'border-b-2 px-3 py-1.5 text-sm transition-colors',
                view === 'consolidation'
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              Consolidation
            </button>
          </div>

          {view === 'schedule' && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <div className="space-y-4 xl:col-span-2">
                <MilestoneStrip projectId={projectId} milestones={data.milestones} />
                <LookAheadList
                  projectId={projectId}
                  items={data.items}
                  rooms={data.rooms}
                />
              </div>
              <div className="xl:col-span-1">
                <div className="h-[60vh] min-h-[420px] xl:sticky xl:top-4 xl:h-[70vh] xl:min-h-[480px]">
                  <RenovationChat projectId={projectId} />
                </div>
              </div>
            </div>
          )}

          {view === 'consolidation' && (
            <ConsolidationView
              projectId={projectId}
              items={data.items}
              rooms={data.rooms}
            />
          )}

          {view === 'rooms' && (
            <RoomTabs
              rooms={data.rooms}
              activeId={activeRoomId}
              onSelect={setActiveRoomId}
              projectId={projectId}
              onChange={invalidate}
            />
          )}

          {view === 'rooms' && <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="space-y-4 xl:col-span-2">
              {activeRoom?.heroUrl && activeRoom.heroMediaId && (
                <HeroPinBoard
                  ref={heroRef}
                  projectId={projectId}
                  roomId={activeRoom.id}
                  mediaId={activeRoom.heroMediaId}
                  heroUrl={activeRoom.heroUrl}
                  items={items}
                  onUnpin={() => {
                    void api
                      .updateRoom(projectId, activeRoom.id, { heroMediaId: null })
                      .then(() => {
                        invalidate();
                        toast.success('Unpinned.');
                      })
                      .catch(() => toast.error('Failed to unpin.'));
                  }}
                />
              )}
              {activeRoom && (
                <>
                  {(() => {
                    const roomRollup = data.budget.byRoom.find(
                      (r) => r.roomId === activeRoom.id,
                    );
                    return roomRollup ? (
                      <BudgetRollupCard
                        scope="room"
                        label={activeRoom.name}
                        rollup={roomRollup}
                      />
                    ) : null;
                  })()}
                  <ItemsTable
                    projectId={projectId}
                    room={activeRoom}
                    items={items}
                    expenses={data.expenses}
                    rooms={data.rooms}
                    heroMediaId={activeRoom.heroMediaId ?? null}
                    onFocusPin={(pinId) => heroRef.current?.focusPin(pinId)}
                    onChange={invalidate}
                  />
                </>
              )}
              {activeRoom && <RoomColorWheel />}
              {activeRoom && (
                <RoomMediaPanel
                  projectId={projectId}
                  roomId={activeRoom.id}
                  items={items}
                  heroMediaId={activeRoom.heroMediaId}
                  onTogglePin={(mediaId) => {
                    const next = activeRoom.heroMediaId === mediaId ? null : mediaId;
                    void api
                      .updateRoom(projectId, activeRoom.id, { heroMediaId: next })
                      .then(() => {
                        invalidate();
                        toast.success(next ? 'Pinned to hub.' : 'Unpinned.');
                      })
                      .catch(() => toast.error('Failed to update pin.'));
                  }}
                />
              )}
            </div>
            <div className="xl:col-span-1">
              <div className="h-[60vh] min-h-[420px] xl:sticky xl:top-4 xl:h-[70vh] xl:min-h-[480px]">
                <RenovationChat projectId={projectId} />
              </div>
            </div>
          </div>}
        </>
      )}
    </div>
  );
}

function AddRoomButton({
  projectId,
  onCreated,
}: {
  projectId: string;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);
  const mutation = useMutation({
    mutationFn: (n: string) => api.createRoom(projectId, { name: n }),
    onSuccess: () => {
      onCreated();
      setName('');
      setOpen(false);
      toast.success('Room added.');
    },
    onError: () => toast.error('Failed to add room.'),
  });

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" /> Add room
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Master bedroom"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim()) mutation.mutate(name.trim());
          if (e.key === 'Escape') setOpen(false);
        }}
        className="w-56"
      />
      <Button
        size="sm"
        onClick={() => name.trim() && mutation.mutate(name.trim())}
        disabled={mutation.isPending || !name.trim()}
      >
        Add
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
        cancel
      </Button>
    </div>
  );
}

function RoomTabs({
  rooms,
  activeId,
  onSelect,
  projectId,
  onChange,
}: {
  rooms: RenovationRoomDto[];
  activeId: string | null;
  onSelect: (id: string) => void;
  projectId: string;
  onChange: () => void;
}) {
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteRoom(projectId, id),
    onSuccess: () => {
      onChange();
      toast.success('Room deleted.');
    },
    onError: () => toast.error('Delete failed (room may have items).'),
  });

  const renameMutation = useMutation({
    mutationFn: (args: { id: string; name: string }) =>
      api.updateRoom(projectId, args.id, { name: args.name }),
    onSuccess: () => {
      onChange();
      toast.success('Room renamed.');
      setEditingId(null);
    },
    onError: () => toast.error('Rename failed.'),
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border">
      {rooms.map((r) => {
        const isActive = activeId === r.id;
        const isEditing = editingId === r.id;
        return (
          <div
            key={r.id}
            className={cn(
              'group flex items-center gap-1 border-b-2 px-3 py-1.5 text-sm transition-colors',
              isActive
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {isEditing ? (
              <Input
                autoFocus
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const next = draftName.trim();
                    if (next && next !== r.name) {
                      renameMutation.mutate({ id: r.id, name: next });
                    } else {
                      setEditingId(null);
                    }
                  } else if (e.key === 'Escape') {
                    setEditingId(null);
                  }
                }}
                onBlur={() => setEditingId(null)}
                className="h-6 w-32 px-1 text-sm"
              />
            ) : (
              <button onClick={() => onSelect(r.id)}>{r.name}</button>
            )}
            {isActive && !isEditing && (
              <>
                <button
                  onClick={() => {
                    setDraftName(r.name);
                    setEditingId(r.id);
                  }}
                  className="opacity-40 hover:opacity-100"
                  title="Rename room"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Delete room "${r.name}"? Items in it are deleted too.`)) {
                      deleteMutation.mutate(r.id);
                    }
                  }}
                  className="opacity-40 hover:opacity-100"
                  title="Delete room"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ItemsTable({
  projectId,
  room,
  items,
  expenses,
  rooms,
  heroMediaId,
  onFocusPin,
  onChange,
}: {
  projectId: string;
  room: RenovationRoomDto;
  items: RenovationItemDto[];
  expenses: RenovationExpenseDto[];
  rooms: RenovationRoomDto[];
  heroMediaId?: string | null;
  onFocusPin?: (pinId: string) => void;
  onChange: () => void;
}) {
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: (activity: string) =>
      api.createItem(projectId, { roomId: room.id, activity }),
    onSuccess: () => {
      onChange();
    },
    onError: () => toast.error('Failed to add row.'),
  });

  const [newRow, setNewRow] = useState('');
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const linkedExpensesByItem = useMemo(() => {
    const map = new Map<string, RenovationExpenseDto[]>();
    for (const e of expenses) {
      if (!e.itemId) continue;
      const list = map.get(e.itemId) ?? [];
      list.push(e);
      map.set(e.itemId, list);
    }
    return map;
  }, [expenses]);

  // Pins on the active hero, indexed by linkedItemId so each row can show its
  // own pin-count badge. When no hero is pinned heroMediaId is null and we
  // skip the query entirely (hook handles enabled flag internally).
  const { pins: heroPins } = useMediaPins({
    projectId,
    roomId: room.id,
    mediaId: heroMediaId ?? null,
  });
  const pinsByItem = useMemo(() => {
    const map = new Map<string, Array<{ id: string; index: number }>>();
    heroPins.forEach((p, i) => {
      if (!p.linkedItemId) return;
      const list = map.get(p.linkedItemId) ?? [];
      list.push({ id: p.id, index: i + 1 });
      map.set(p.linkedItemId, list);
    });
    return map;
  }, [heroPins]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 14 }}
    >
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="border-b border-border bg-muted/30 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="sticky left-0 z-20 w-56 min-w-[14rem] border-r border-border bg-muted/30 p-2 text-left font-normal">
                    Activity
                  </th>
                  <th className="p-2 text-left font-normal">Status</th>
                  <th className="p-2 text-right font-normal">Est cost</th>
                  <th className="p-2 text-right font-normal">Actual</th>
                  <th className="p-2 text-center font-normal">Mat ord</th>
                  <th className="p-2 text-left font-normal">ETA</th>
                  <th className="p-2 text-left font-normal">Vendor</th>
                  <th className="p-2 text-left font-normal">Product</th>
                  <th className="p-2 text-left font-normal">Notes</th>
                  <th className="w-8 p-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-xs italic text-muted-foreground">
                      No items in this room yet.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <ItemRow
                      key={item.id}
                      projectId={projectId}
                      item={item}
                      linkedExpenses={linkedExpensesByItem.get(item.id) ?? []}
                      pins={pinsByItem.get(item.id) ?? []}
                      onFocusPin={onFocusPin}
                      expanded={expandedItemId === item.id}
                      onToggleExpand={() =>
                        setExpandedItemId(expandedItemId === item.id ? null : item.id)
                      }
                      onChange={onChange}
                      rooms={rooms}
                    />
                  ))
                )}
                <tr className="border-t border-border bg-muted/10">
                  <td colSpan={10} className="p-2">
                    <div className="flex items-center gap-2">
                      <Input
                        value={newRow}
                        onChange={(e) => setNewRow(e.target.value)}
                        placeholder="Add new row..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newRow.trim()) {
                            create.mutate(newRow.trim());
                            setNewRow('');
                          }
                        }}
                        className="h-8 border-0 bg-transparent shadow-none focus-visible:ring-0"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!newRow.trim() || create.isPending}
                        onClick={() => {
                          if (newRow.trim()) {
                            create.mutate(newRow.trim());
                            setNewRow('');
                          }
                        }}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      {/* Silence unused warning */}
      <span className="hidden">{queryClient ? '' : ''}</span>
    </motion.div>
  );
}

function ItemRow({
  projectId,
  item,
  linkedExpenses,
  pins,
  onFocusPin,
  expanded,
  onToggleExpand,
  onChange,
}: {
  projectId: string;
  item: RenovationItemDto;
  linkedExpenses: RenovationExpenseDto[];
  pins?: Array<{ id: string; index: number }>;
  onFocusPin?: (pinId: string) => void;
  expanded: boolean;
  onToggleExpand: () => void;
  onChange: () => void;
  rooms: RenovationRoomDto[];
}) {
  const update = useMutation({
    mutationFn: (patch: Partial<RenovationItemDto>) =>
      api.updateItem(projectId, item.id, patch),
    onSuccess: () => onChange(),
    onError: () => toast.error('Save failed.'),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteItem(projectId, item.id),
    onSuccess: () => {
      onChange();
      toast.success('Row deleted.');
    },
    onError: () => toast.error('Delete failed.'),
  });

  return (
    <>
      <tr className={cn('border-b border-border hover:bg-muted/10', rowSignalClass(item))}>
        <td className="sticky left-0 z-10 w-56 min-w-[14rem] border-r border-border bg-card p-2 align-top">
          <div className="flex items-start gap-1.5">
            <button
              onClick={onToggleExpand}
              className="mt-0.5 flex-none text-muted-foreground hover:text-foreground"
              title={expanded ? 'Collapse' : 'Expand linked expenses'}
            >
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
            <div className="min-w-0 flex-1">
              <EditableText
                value={item.activity}
                onSave={(v) => update.mutate({ activity: v })}
                className="font-medium"
              />
            </div>
            {pins && pins.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {pins.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => onFocusPin?.(p.id)}
                    title="Show on reference image"
                    className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground hover:bg-primary/90"
                  >
                    {p.index}
                  </button>
                ))}
              </div>
            )}
          </div>
        </td>
        <td className="p-2 align-top">
          <Select
            value={item.status}
            onValueChange={(v) => update.mutate({ status: v as RenovationItemStatus })}
          >
            <SelectTrigger className="h-7 w-36 border-0 bg-transparent px-2 py-0 text-xs shadow-none focus:ring-0">
              <SelectValue>
                <Badge variant={STATUS_VARIANT[item.status]}>{item.status.toLowerCase().replace('_', ' ')}</Badge>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.toLowerCase().replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="p-2 text-right align-top">
          <EditableMoney
            value={item.estimatedCost}
            onSave={(v) => update.mutate({ estimatedCost: v })}
          />
        </td>
        <td className="p-2 text-right align-top">
          <EditableMoney
            value={item.actualCost}
            onSave={(v) => update.mutate({ actualCost: v })}
          />
          {item.linkedExpensesSum > 0 && (
            <div
              className={cn(
                'mt-0.5 inline-flex items-center gap-1 text-[10px]',
                item.divergenceFlag ? 'text-amber-400' : 'text-muted-foreground',
              )}
            >
              {item.divergenceFlag && <AlertTriangle className="h-2.5 w-2.5" />}
              linked: {usdCompact(item.linkedExpensesSum)}
            </div>
          )}
        </td>
        <td className="p-2 text-center align-top">
          <input
            type="checkbox"
            checked={item.materialsOrdered}
            onChange={(e) => update.mutate({ materialsOrdered: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-border bg-background"
          />
        </td>
        <td className="p-2 align-top">
          <EditableText
            value={item.materialsEta ?? ''}
            placeholder="YYYY-MM-DD"
            onSave={(v) => update.mutate({ materialsEta: v || null })}
            className="text-xs"
          />
        </td>
        <td className="p-2 align-top">
          <EditableText
            value={item.vendor ?? ''}
            onSave={(v) => update.mutate({ vendor: v || null })}
            className="text-xs"
          />
        </td>
        <td className="p-2 align-top">
          <div className="flex items-center gap-1.5">
            <div className="min-w-0 flex-1">
              <EditableText
                value={item.productUrl ?? ''}
                placeholder="Add link"
                onSave={(v) => update.mutate({ productUrl: v ? v.trim() : null })}
                className="text-xs"
              />
            </div>
            {item.productUrl && (
              <a
                href={
                  /^https?:\/\//i.test(item.productUrl)
                    ? item.productUrl
                    : `https://${item.productUrl}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex-none text-muted-foreground hover:text-foreground"
                title="Open product page"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </td>
        <td className="p-2 align-top">
          <EditableText
            value={item.notes ?? ''}
            onSave={(v) => update.mutate({ notes: v || null })}
            className="text-xs"
            multiline
          />
        </td>
        <td className="p-2 align-top">
          <button
            onClick={() => {
              if (confirm(`Delete "${item.activity}"?`)) remove.mutate();
            }}
            className="text-muted-foreground hover:text-destructive"
            title="Delete row"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border bg-muted/20">
          <td colSpan={10} className="p-3">
            <LinkedExpensesPanel
              projectId={projectId}
              itemId={item.id}
              roomId={item.roomId}
              expenses={linkedExpenses}
              onChange={onChange}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function LinkedExpensesPanel({
  projectId,
  itemId,
  roomId,
  expenses,
  onChange,
}: {
  projectId: string;
  itemId: string;
  roomId: string;
  expenses: RenovationExpenseDto[];
  onChange: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [vendor, setVendor] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.createExpense(projectId, {
        amount: Number(amount),
        date,
        vendor: vendor || null,
        description: description || null,
        roomId,
        itemId,
      }),
    onSuccess: () => {
      onChange();
      setAmount('');
      setVendor('');
      setDescription('');
      toast.success('Expense logged.');
    },
    onError: () => toast.error('Failed to log expense.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteExpense(projectId, id),
    onSuccess: () => onChange(),
    onError: () => toast.error('Delete failed.'),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Receipt className="h-3.5 w-3.5" />
        Linked expenses
      </div>
      {expenses.length === 0 ? (
        <p className="text-xs italic text-muted-foreground">No expenses linked to this item yet.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left font-normal">Date</th>
              <th className="text-left font-normal">Vendor</th>
              <th className="text-left font-normal">Description</th>
              <th className="text-right font-normal">Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id} className="border-t border-border">
                <td className="py-1">{e.date}</td>
                <td className="py-1">{e.vendor ?? '-'}</td>
                <td className="py-1">{e.description ?? '-'}</td>
                <td className="py-1 text-right">{usdCompact(e.amount)}</td>
                <td className="py-1 text-right">
                  <button
                    onClick={() => remove.mutate(e.id)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="grid grid-cols-2 items-center gap-2 border-t border-border pt-3 sm:grid-cols-[1fr,1fr,2fr,1fr,auto]">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-8"
        />
        <Input
          placeholder="Vendor"
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          className="h-8"
        />
        <Input
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="h-8"
        />
        <Input
          placeholder="Amount"
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="h-8 text-right"
        />
        <Button
          size="sm"
          disabled={!amount || create.isPending}
          onClick={() => create.mutate()}
        >
          Log
        </Button>
      </div>
    </div>
  );
}

function EditableText({
  value,
  onSave,
  placeholder,
  className,
  multiline,
}: {
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
  className?: string;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className={cn(
          'block w-full truncate text-left',
          !value && 'italic text-muted-foreground',
          className,
        )}
      >
        {value || placeholder || '-'}
      </button>
    );
  }
  const commit = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };
  if (multiline) {
    return (
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDraft(value);
            setEditing(false);
          }
        }}
        className={cn(
          'w-full rounded border border-input bg-background px-2 py-1',
          className,
        )}
      />
    );
  }
  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') {
          setDraft(value);
          setEditing(false);
        }
      }}
      className={cn(
        'w-full rounded border border-input bg-background px-2 py-1',
        className,
      )}
      placeholder={placeholder}
    />
  );
}

function EditableMoney({
  value,
  onSave,
}: {
  value: number | null;
  onSave: (v: number | null) => void;
}) {
  const display = value === null ? '-' : usdCompact(value);
  return (
    <EditableText
      value={value === null ? '' : String(value)}
      placeholder={display === '-' ? '' : display}
      onSave={(v) => {
        const trimmed = v.trim();
        if (trimmed === '') onSave(null);
        else {
          const n = Number(trimmed);
          if (Number.isFinite(n)) onSave(n);
        }
      }}
      className="text-right tabular-nums"
    />
  );
}

// Silence unused
const _x: ReactNode = null;
void _x;

/**
 * Expenses auto-extracted from invoice emails (source=EMAIL) that haven't yet
 * been linked to a room/item. Surfaces above the rooms so Karl can confirm
 * extraction worked and quickly link them.
 */
function EmailExtractedExpenses({ expenses }: { expenses: RenovationExpenseDto[] }) {
  const fromEmail = useMemo(
    () =>
      expenses
        .filter((e) => e.source === 'EMAIL' && !e.itemId)
        .sort((a, b) => (a.date < b.date ? 1 : -1)),
    [expenses],
  );
  if (fromEmail.length === 0) return null;
  const totalExtracted = fromEmail.reduce((s, e) => s + e.amount, 0);
  return (
    <Card className="border-emerald-500/40 bg-emerald-500/5">
      <CardContent className="p-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <p className="text-sm font-semibold">
              Auto-extracted from email · {fromEmail.length} invoice
              {fromEmail.length === 1 ? '' : 's'}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            {usdCompact(totalExtracted)} total · unlinked to rooms
          </p>
        </div>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left font-normal">Date</th>
              <th className="text-left font-normal">Vendor</th>
              <th className="text-left font-normal">Description</th>
              <th className="text-right font-normal">Amount</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {fromEmail.map((e) => (
              <tr key={e.id} className="border-t border-emerald-500/20">
                <td className="py-1.5">{e.date}</td>
                <td className="py-1.5">{e.vendor ?? '-'}</td>
                <td className="py-1.5">{e.description ?? '-'}</td>
                <td className="py-1.5 text-right tabular-nums">{usdCompact(e.amount)}</td>
                <td className="py-1.5 text-right">
                  {e.relatedId && e.relatedTable === 'messages' && (
                    <Link
                      to={`/inbox?messageId=${e.relatedId}`}
                      className="text-[10px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    >
                      view email
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
